import { create } from "zustand";
import {
  EnqueueFinancialPayload,
  FinancialRunData,
  FinancialRunStatus,
  RerunFinancialPayload,
  enqueueFinancial,
  rerunFinancial,
} from "../api/dataPipeline";

export interface FinancialEntry {
  runId: string;
  cin: string;
  jobId: string;
  status: FinancialRunStatus;
  phase: string | null;
  resultUrl: string | null;
  urlExpiry: string | null;
  error: string | null;
  requestedAt: number;
}

export type FinancialRequestResult =
  | { kind: "started"; entry: FinancialEntry }
  | { kind: "running"; entry: FinancialEntry }
  | { kind: "error"; error: string };

const TERMINAL_STATUSES: FinancialRunStatus[] = ["COMPLETED", "FAILED"];

export function isFinancialRunInFlight(
  entry: FinancialEntry | undefined,
): boolean {
  return !!entry && !TERMINAL_STATUSES.includes(entry.status);
}

// Minimal data needed to start tracking a run enqueued outside this store
// (e.g. one item of a batch enqueue response).
export interface TrackFinancialRunInit {
  runId: string;
  cin: string;
  jobId: string;
  status: string;
}

interface FinancialStore {
  runs: Record<string, FinancialEntry>;
  enqueueRun: (
    payload: EnqueueFinancialPayload,
  ) => Promise<FinancialRequestResult>;
  // High-quality re-extraction of a prior run's AOC-4s. cin comes from the
  // source run; omit aoc4Keys to re-run all of them.
  rerunRun: (
    sourceRunId: string,
    cin: string,
    payload?: RerunFinancialPayload,
  ) => Promise<FinancialRequestResult>;
  // Register an already-enqueued run so the manager starts polling it.
  trackRun: (init: TrackFinancialRunInit) => void;
  applyStatus: (data: FinancialRunData) => void;
  clearRun: (runId: string) => void;
  getRunsForCin: (cin: string) => FinancialEntry[];
}

export const useFinancialStore = create<FinancialStore>((set, get) => ({
  runs: {},

  enqueueRun: async (payload) => {
    try {
      const res = await enqueueFinancial(payload);
      const existing = get().runs[res.runId];
      const entry: FinancialEntry = {
        runId: res.runId,
        cin: payload.cin,
        jobId: res.jobId,
        status: (res.status as FinancialRunStatus) ?? "QUEUED",
        phase: null,
        resultUrl: null,
        urlExpiry: null,
        error: null,
        requestedAt: existing?.requestedAt ?? Date.now(),
      };
      set((s) => ({ runs: { ...s.runs, [entry.runId]: entry } }));
      if (res.deduped) return { kind: "running", entry };
      return { kind: "started", entry };
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to enqueue financial extraction.";
      return { kind: "error", error: msg };
    }
  },

  rerunRun: async (sourceRunId, cin, payload) => {
    try {
      const res = await rerunFinancial(sourceRunId, payload);
      const existing = get().runs[res.runId];
      const entry: FinancialEntry = {
        runId: res.runId,
        cin,
        jobId: res.jobId ?? "",
        status: (res.status as FinancialRunStatus) ?? "QUEUED",
        phase: null,
        resultUrl: null,
        urlExpiry: null,
        error: null,
        requestedAt: existing?.requestedAt ?? Date.now(),
      };
      set((s) => ({ runs: { ...s.runs, [entry.runId]: entry } }));
      if (res.deduped) return { kind: "running", entry };
      return { kind: "started", entry };
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to start financial rerun.";
      return { kind: "error", error: msg };
    }
  },

  trackRun: (init) => {
    set((s) => {
      const existing = s.runs[init.runId];
      const entry: FinancialEntry = {
        runId: init.runId,
        cin: init.cin,
        jobId: init.jobId,
        status: (init.status as FinancialRunStatus) ?? "QUEUED",
        phase: existing?.phase ?? null,
        resultUrl: existing?.resultUrl ?? null,
        urlExpiry: existing?.urlExpiry ?? null,
        error: existing?.error ?? null,
        requestedAt: existing?.requestedAt ?? Date.now(),
      };
      return { runs: { ...s.runs, [entry.runId]: entry } };
    });
  },

  applyStatus: (data) => {
    set((s) => {
      const existing = s.runs[data.runId];
      if (!existing) return s;
      const next: FinancialEntry = {
        ...existing,
        status: data.status,
        phase: data.phase,
        resultUrl: data.resultUrl,
        urlExpiry: data.urlExpiry,
      };
      return { runs: { ...s.runs, [data.runId]: next } };
    });
  },

  clearRun: (runId) => {
    set((s) => {
      const { [runId]: _removed, ...rest } = s.runs;
      return { runs: rest };
    });
  },

  getRunsForCin: (cin) =>
    Object.values(get().runs).filter((r) => r.cin === cin),
}));
